// =============================================================================
// components/kanban/KanbanColumn.tsx — Coluna do Kanban.
//
// Header: caption-style, badge com o TOTAL do servidor (F55-S08), não o
//   número de cards carregados.
// Rodapé: "Carregar mais" quando a coluna tem mais páginas.
// Body: bg-elev-2, elev-1, lista de cards droppable.
// Drop zones:
//   - Coluna válida: border-dashed brand-azul
//   - Coluna inválida: border-dashed danger
// Empty state: SVG inline + caption.
// Skeleton de loading: 3 cards placeholder.
// =============================================================================

import { useDroppable } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import type { Appointment } from '@elemento/shared-schemas';
import * as React from 'react';

import { remainingInStage } from '../../hooks/kanban/board';
import type { KanbanCard, KanbanStage } from '../../hooks/kanban/types';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';

import { KanbanCard as KanbanCardComponent } from './KanbanCard';

// ── Tipos ─────────────────────────────────────────────────────────────────────

interface KanbanColumnProps {
  stage: KanbanStage;
  /** Cards já carregados da coluna (páginas acumuladas, ordem do servidor). */
  cards: KanbanCard[];
  /**
   * Total da coluna no servidor, com os mesmos filtros e escopo (F55-S08).
   * `undefined` enquanto a contagem carrega. NUNCA `cards.length` — a coluna
   * mostra só as páginas carregadas.
   */
  total?: number | undefined;
  /** Há mais páginas no servidor. */
  hasMore?: boolean | undefined;
  /** Carrega a próxima página; rejeita em erro de rede. */
  onLoadMore?: (() => Promise<void>) | undefined;
  isLoading?: boolean | undefined;
  isOver?: boolean | undefined;
  isInvalid?: boolean | undefined;
  onCardClick: (card: KanbanCard) => void;
  /** leadId → próxima ação (F33-S07). `undefined` = gate fechado, chip nunca renderiza. */
  nextActionByLead?: Map<string, Appointment> | undefined;
  now?: Date | undefined;
}

// ── Empty State ────────────────────────────────────────────────────────────────

function EmptyState(): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 py-8 px-4">
      {/* Ilustração SVG inline minimalista */}
      <svg
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <rect
          x="8"
          y="8"
          width="32"
          height="32"
          rx="6"
          stroke="var(--border-strong)"
          strokeWidth="1.5"
          strokeDasharray="4 3"
        />
        <path
          d="M18 24h12M24 18v12"
          stroke="var(--border-strong)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
      <p
        className="font-sans text-ink-4 text-center"
        style={{
          fontSize: 'var(--text-xs)',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        Sem cards nessa etapa
      </p>
    </div>
  );
}

// ── Card Skeleton ──────────────────────────────────────────────────────────────

function CardSkeleton(): React.JSX.Element {
  return (
    <div
      className="rounded-md border border-border bg-[var(--bg-elev-1)] p-3 flex flex-col gap-2 animate-pulse"
      style={{ boxShadow: 'var(--elev-1)' }}
    >
      <div className="h-3.5 bg-surface-muted rounded-xs w-3/4" />
      <div className="h-3 bg-surface-muted rounded-xs w-1/2" />
      <div className="h-3 bg-surface-muted rounded-xs w-1/3" />
      <div className="flex justify-between pt-1 border-t border-border-subtle">
        <div className="h-3 bg-surface-muted rounded-xs w-1/4" />
        <div className="h-3 bg-surface-muted rounded-xs w-1/6" />
      </div>
    </div>
  );
}

// ── Contagem badge ─────────────────────────────────────────────────────────────

interface CountBadgeProps {
  /** `undefined` = contagem ainda carregando. */
  count: number | undefined;
}

const countFormatter = new Intl.NumberFormat('pt-BR');

function CountBadge({ count }: CountBadgeProps): React.JSX.Element {
  if (count === undefined) {
    return (
      <span
        className="inline-block h-[18px] w-7 rounded-pill bg-[var(--surface-muted)] animate-pulse"
        aria-hidden="true"
      />
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center',
        'font-sans font-bold text-ink-3',
        'bg-[var(--surface-muted)] rounded-pill',
        'px-2 py-0.5 min-w-[20px]',
      )}
      style={{
        fontSize: 'var(--text-xs)',
        boxShadow: 'var(--elev-1)',
        fontVariantNumeric: 'tabular-nums',
      }}
      aria-label={`${countFormatter.format(count)} ${count === 1 ? 'card' : 'cards'}`}
    >
      {countFormatter.format(count)}
    </span>
  );
}

// ── Carregar mais ──────────────────────────────────────────────────────────────

interface LoadMoreProps {
  stageName: string;
  remaining: number;
  onLoadMore: () => Promise<void>;
}

/**
 * Rodapé da coluna — próxima página (F55-S08). Estado de carregando e de erro
 * locais à coluna: falhar uma coluna não derruba o board.
 */
function LoadMore({ stageName, remaining, onLoadMore }: LoadMoreProps): React.JSX.Element {
  const [state, setState] = React.useState<'idle' | 'loading' | 'error'>('idle');

  const handleClick = (): void => {
    setState('loading');
    onLoadMore().then(
      () => setState('idle'),
      () => setState('error'),
    );
  };

  const label =
    state === 'loading'
      ? 'Carregando…'
      : state === 'error'
        ? 'Falhou — tentar de novo'
        : remaining > 0
          ? `Carregar mais · ${countFormatter.format(remaining)} restantes`
          : 'Carregar mais';

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn('w-full', state === 'error' && 'text-danger hover:text-danger')}
      onClick={handleClick}
      disabled={state === 'loading'}
      aria-busy={state === 'loading'}
      aria-label={`${label} na coluna ${stageName}`}
    >
      {label}
    </Button>
  );
}

// ── KanbanColumn ──────────────────────────────────────────────────────────────

/**
 * Coluna do Kanban — wrapper droppable para SortableContext.
 *
 * isOver + isInvalid controlam o indicador visual da drop zone:
 * - Válido: border-dashed brand-azul
 * - Inválido: border-dashed danger
 */
export function KanbanColumn({
  stage,
  cards,
  total,
  hasMore = false,
  onLoadMore,
  isLoading = false,
  isOver = false,
  isInvalid = false,
  onCardClick,
  nextActionByLead,
  now,
}: KanbanColumnProps): React.JSX.Element {
  const cardIds = cards.map((c) => c.id);

  // useDroppable garante que o body da coluna receba drops mesmo quando vazio.
  // Sem isso, `over` no DragEnd vem null ao soltar em coluna sem cards.
  const { setNodeRef, isOver: isDirectlyOver } = useDroppable({ id: stage.id });

  return (
    <div
      className="flex flex-col gap-0 min-w-[260px] max-w-[300px] w-full flex-shrink-0"
      role="region"
      aria-label={`Coluna ${stage.name}`}
    >
      {/* ── Header da coluna ─────────────────────────────────────── */}
      <div className="flex items-center justify-between px-3 py-2 mb-2">
        <h2
          className="font-sans font-bold text-ink-2 uppercase tracking-wider"
          style={{ fontSize: 'var(--text-xs)' }}
        >
          {stage.name}
        </h2>
        <CountBadge count={total} />
      </div>

      {/* ── Body droppable ───────────────────────────────────────── */}
      <div
        ref={setNodeRef}
        className={cn(
          'flex flex-col gap-2 rounded-md p-2',
          'bg-[var(--bg-elev-2)] min-h-[120px] flex-1',
          'border border-transparent',
          'transition-[border-color,background] duration-fast ease-out',
          // Drop zone indicators — só destaca quando o ponteiro está SOBRE esta coluna
          isDirectlyOver && !isInvalid && 'border-dashed border-azul bg-[var(--info-bg)]',
          isOver && isInvalid && 'border-dashed border-danger bg-[var(--danger-bg)]',
        )}
        style={{ boxShadow: 'var(--elev-1)' }}
      >
        {isLoading ? (
          // Skeleton loading — 3 cards placeholder
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : (
          // Sem `strategy`: evita shift dos outros cards (sensação de cursor
          // 1-2 slots à frente). Drop entre colunas é detectado pelo
          // useDroppable da coluna + closestCorners do DndContext.
          <SortableContext items={cardIds}>
            {cards.length === 0 ? (
              <EmptyState />
            ) : (
              cards.map((card) => (
                <KanbanCardComponent
                  key={card.id}
                  card={card}
                  onClick={onCardClick}
                  nextAction={nextActionByLead?.get(card.leadId)}
                  now={now}
                />
              ))
            )}
            {hasMore && onLoadMore && (
              <LoadMore
                stageName={stage.name}
                remaining={remainingInStage(total, cards.length)}
                onLoadMore={onLoadMore}
              />
            )}
          </SortableContext>
        )}
      </div>
    </div>
  );
}
